// ============================================================================
// ECHARTS SETUP: TREE-SHAKEN IMPORTS + THEMES
// ============================================================================
// This module is imported once by the EChart wrapper. It:
// 1. Registers only the chart types / components we actively use (tree-shakes
//    everything else out of the bundle). Add new chart types here when
//    building new widgets.
// 2. Registers 'prima-light' and 'prima-dark' themes so charts match app theme.

import * as echarts from 'echarts/core';
import { BarChart, PieChart, LineChart, SankeyChart } from 'echarts/charts';
import {
  GridComponent,
  TooltipComponent,
  LegendComponent,
  TitleComponent,
  DatasetComponent,
  DataZoomComponent,
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';

// ---------------------------------------------------------------------------
// Chart & component registration (one-time)
// ---------------------------------------------------------------------------

echarts.use([
  BarChart,
  PieChart,
  LineChart,
  SankeyChart,
  GridComponent,
  TooltipComponent,
  LegendComponent,
  TitleComponent,
  DatasetComponent,
  DataZoomComponent,
  CanvasRenderer,
]);

// ---------------------------------------------------------------------------
// Shared palette
// ---------------------------------------------------------------------------
// Engagement-status colors are applied per-data-point in widgets, not here.
// This palette is the default fallback when series don't set itemStyle.

const PALETTE = [
  '#3b82f6', // blue
  '#16a34a', // green
  '#d97706', // amber
  '#dc2626', // red
  '#0ea5e9', // sky
  '#8b5cf6', // violet
  '#64748b', // slate
  '#14b8a6', // teal
];

// ---------------------------------------------------------------------------
// Themes
// ---------------------------------------------------------------------------

echarts.registerTheme('prima-light', {
  color: PALETTE,
  backgroundColor: 'transparent',
  textStyle: { color: '#1e293b', fontFamily: 'inherit' },
  title: { textStyle: { color: '#1e293b' } },
  legend: { textStyle: { color: '#64748b', fontSize: 11 } },
  tooltip: {
    backgroundColor: '#ffffff',
    borderColor: '#e2e8f0',
    borderWidth: 1,
    textStyle: { color: '#1e293b', fontSize: 12 },
    extraCssText: 'box-shadow: 0 4px 12px rgba(0,0,0,0.08); border-radius: 6px;',
  },
  axisPointer: { lineStyle: { color: '#94a3b8' } },
  categoryAxis: {
    axisLine: { lineStyle: { color: '#cbd5e1' } },
    axisTick: { lineStyle: { color: '#cbd5e1' } },
    axisLabel: { color: '#64748b', fontSize: 11 },
    splitLine: { lineStyle: { color: '#f1f5f9', type: 'dashed' } },
  },
  valueAxis: {
    axisLine: { lineStyle: { color: '#cbd5e1' } },
    axisTick: { lineStyle: { color: '#cbd5e1' } },
    axisLabel: { color: '#64748b', fontSize: 11 },
    splitLine: { lineStyle: { color: '#f1f5f9', type: 'dashed' } },
  },
});

echarts.registerTheme('prima-dark', {
  color: PALETTE,
  backgroundColor: 'transparent',
  textStyle: { color: '#e2e8f0', fontFamily: 'inherit' },
  title: { textStyle: { color: '#e2e8f0' } },
  legend: { textStyle: { color: '#94a3b8', fontSize: 11 } },
  tooltip: {
    backgroundColor: '#1e293b',
    borderColor: '#334155',
    borderWidth: 1,
    textStyle: { color: '#e2e8f0', fontSize: 12 },
    extraCssText: 'box-shadow: 0 4px 12px rgba(0,0,0,0.4); border-radius: 6px;',
  },
  axisPointer: { lineStyle: { color: '#64748b' } },
  categoryAxis: {
    axisLine: { lineStyle: { color: '#334155' } },
    axisTick: { lineStyle: { color: '#334155' } },
    axisLabel: { color: '#94a3b8', fontSize: 11 },
    splitLine: { lineStyle: { color: '#1e293b', type: 'dashed' } },
  },
  valueAxis: {
    axisLine: { lineStyle: { color: '#334155' } },
    axisTick: { lineStyle: { color: '#334155' } },
    axisLabel: { color: '#94a3b8', fontSize: 11 },
    splitLine: { lineStyle: { color: '#1e293b', type: 'dashed' } },
  },
});

// Re-export echarts so the wrapper (and callers) use the same tree-shaken instance.
export { echarts };
export const PRIMA_PALETTE = PALETTE;
